import { createTheme, type Shadows } from '@mui/material/styles';

/**
 * Autopilot-Theme: Basis wie Nidus (@nidus/ui theme.ts, nachgebaut, nicht importiert),
 * Teal durchgehend durch Autopilot-Rot ersetzt. Kontraste (WCAG AA) geprüft:
 * #B42332 auf Weiß 6,5:1 · Weiß auf #B42332 6,5:1 · #121838 auf #F7F8FA 16,3:1 ·
 * #535C70 auf Weiß 6,7:1 · #1E7A4E auf #E6F4EC 4,7:1 · #7A5410 auf #FBF1DD 6,0:1 ·
 * #A51D1D auf #FBE7E7 6,3:1 · #2A4FA8 auf #E8EEFB 6,5:1.
 */
export const red = {
  50: '#FFF1F2',
  100: '#FDE0E3',
  200: '#F6B9C0',
  300: '#E57F8B',
  400: '#CC4453',
  500: '#B42332',
  600: '#921D29',
  700: '#741723',
  800: '#55111A',
  900: '#3A0B11',
} as const;

export const navy = {
  50: '#EEF0F7',
  100: '#D6DAEC',
  200: '#A9B1D6',
  300: '#6E79B0',
  400: '#404C88',
  500: '#2A3670',
  600: '#1F2A60',
  700: '#18204C',
  800: '#121838',
  900: '#0C1026',
} as const;

export const status = {
  success: { fg: '#1E7A4E', bg: '#E6F4EC', border: '#B7E0C8', main: '#2FA36B' },
  warning: { fg: '#7A5410', bg: '#FBF1DD', border: '#EAD9A0', main: '#E0A23B' },
  error: { fg: '#A51D1D', bg: '#FBE7E7', border: '#F3C7C7', main: '#C42626' },
  info: { fg: '#2A4FA8', bg: '#E8EEFB', border: '#C7D6F5', main: '#3F6FD6' },
  neutral: { fg: '#4C5466', bg: '#EEF0F4', border: '#CBD1DC', main: '#6B7488' },
} as const;

const fontDisplay = "'Jost', 'Century Gothic', 'Futura', system-ui, sans-serif";
const fontBody = "'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif";

const softShadows = Array.from({ length: 25 }, (_, i) => {
  if (i === 0) return 'none';
  if (i <= 2) return '0 1px 2px rgba(31,42,96,.06)';
  if (i <= 4) return '0 2px 8px -2px rgba(31,42,96,.10)';
  if (i <= 8) return '0 8px 24px -8px rgba(31,42,96,.18)';
  if (i <= 16) return '0 18px 48px -12px rgba(31,42,96,.22)';
  return '0 32px 70px -18px rgba(31,42,96,.28)';
}) as Shadows;

const focusRing = `0 0 0 3px rgba(180, 35, 50, .35)`;

export const autopilotTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: red[500], dark: red[600], light: red[300], contrastText: '#FFFFFF' },
    secondary: { main: navy[600], dark: navy[700], light: navy[400], contrastText: '#FFFFFF' },
    warning: { main: status.warning.main, dark: status.warning.fg, contrastText: '#121838' },
    error: { main: status.error.main, dark: status.error.fg, contrastText: '#FFFFFF' },
    success: { main: status.success.main, dark: status.success.fg, contrastText: '#FFFFFF' },
    info: { main: status.info.main, dark: status.info.fg, contrastText: '#FFFFFF' },
    background: { default: '#F7F8FA', paper: '#FFFFFF' },
    text: { primary: '#121838', secondary: '#535C70' },
    divider: '#E6E9EF',
    action: { hover: 'rgba(180, 35, 50, 0.06)', selected: 'rgba(180, 35, 50, 0.10)', focus: 'rgba(180, 35, 50, 0.14)' },
  },
  typography: {
    fontFamily: fontBody,
    fontSize: 14,
    h1: { fontFamily: fontDisplay, fontWeight: 500, fontSize: '2.5rem', lineHeight: 1.08, letterSpacing: '-0.02em' },
    h2: { fontFamily: fontDisplay, fontWeight: 500, fontSize: '2rem', lineHeight: 1.12, letterSpacing: '-0.02em' },
    h3: { fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.75rem', lineHeight: 1.15, letterSpacing: '-0.01em' },
    h4: { fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.375rem', lineHeight: 1.2 },
    h5: { fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.15rem', lineHeight: 1.3 },
    h6: { fontFamily: fontBody, fontWeight: 700, fontSize: '1rem', lineHeight: 1.35 },
    subtitle1: { fontWeight: 600, fontSize: '1rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem' },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    overline: { fontFamily: fontDisplay, fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.18em', textTransform: 'uppercase' },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
  },
  shape: { borderRadius: 10 },
  shadows: softShadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#F7F8FA', color: '#121838' },
        ':focus-visible': { outlineColor: red[500] },
        a: { color: red[600] },
      },
    },
    MuiButtonBase: { defaultProps: { disableRipple: false } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
          fontWeight: 600,
          paddingInline: 18,
          '&.Mui-focusVisible': { boxShadow: focusRing },
        },
        containedPrimary: {
          '&:hover': { backgroundColor: red[600] },
          '&:active': { backgroundColor: red[700] },
        },
        outlinedPrimary: {
          borderColor: red[200],
          '&:hover': { backgroundColor: red[50], borderColor: red[500] },
          '&:active': { backgroundColor: red[100] },
        },
        textPrimary: { '&:hover': { backgroundColor: red[50] }, '&:active': { backgroundColor: red[100] } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { '&.Mui-focusVisible': { boxShadow: focusRing } } } },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #E1E5EC',
          boxShadow: '0 2px 8px -2px rgba(31,42,96,.10)',
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: { styleOverrides: { rounded: { borderRadius: 16 }, root: { backgroundImage: 'none' } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
    MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: red[500], borderWidth: 1.5 },
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { '&.Mui-focused': { color: red[600] } } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E1E5EC', color: '#121838' },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: red[500], height: 3, borderRadius: 3 } } },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          '&.Mui-selected': { color: red[600] },
          '&.Mui-focusVisible': { backgroundColor: red[50] },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 700, color: '#4C5466', whiteSpace: 'nowrap', backgroundColor: '#FAFBFC' },
        root: { borderBottomColor: '#EEF0F4' },
      },
    },
    MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: red[50] } } } },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { color: red[600], fontWeight: 600, '&:focus-visible': { outline: `2px solid ${red[500]}`, outlineOffset: 2 } } },
    },
    MuiSlider: {
      styleOverrides: {
        thumb: { '&.Mui-focusVisible, &:hover': { boxShadow: `0 0 0 8px rgba(180,35,50,.16)` } },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1 } },
      },
    },
    MuiCheckbox: { defaultProps: { color: 'primary' } },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&.Mui-selected': { backgroundColor: red[50], color: red[700] },
          '&.Mui-selected:hover': { backgroundColor: red[100] },
        },
      },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 12, alignItems: 'flex-start' } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: navy[800], fontSize: '0.8rem' } } },
  },
});
